import { useState, type ReactNode } from 'react';

import { BottomSheet } from '@/components/BottomSheet';
import {
  CONSENT_TEXT,
  CONSENT_VERSION,
  TRANSFER_CONSENT_TEXT,
  TRANSFER_CONSENT_VERSION,
} from '@/config/consent';
import { PhotoPicker } from '@/features/upload/PhotoPicker';
import { usePhotoSelection } from '@/features/upload/usePhotoSelection';
import type { Collection, CollectionOptionGroup, UserSelections } from '@/types/collection';
import type { UploadConsent } from '@/types/generation';

import { OptionCard } from './OptionCard';
import { SummaryRow } from './SummaryRow';
import {
  defaultSelections,
  missingGroups,
  optionBlockReason,
  presetSelections,
  selectionIssues,
  toggleOption,
  toUserSelections,
  type SelectionPreset,
} from './wizard-state';

/**
 * Экран создания портрета.
 *
 * Одна страница: фотографии, все параметры коллекции строками и две главные
 * кнопки. Карточки выбора живут в шторке — слое поверх этой же страницы, —
 * поэтому выбор параметра никуда не уводит и его видно в общей сводке.
 *
 * Экран ничего не знает про Riviera 1960s: и строки, и шторки строятся по
 * optionGroups коллекции. Новая коллекция с другим набором осей отрисуется
 * здесь без единой правки.
 */
export interface CreateRequest {
  selections: UserSelections;
  photos: File[];
  consent: UploadConsent;
}

interface CreateScreenProps {
  collection: Collection;
  status: 'idle' | 'pending';
  errorMessage?: string | null | undefined;
  /** Выбор прошлой работы — его приносит «Повторить» с экрана результата. */
  preset?: SelectionPreset | undefined;
  /**
   * Остаток кредитов, если он известен. undefined — баланс ещё не загружен:
   * тогда экран не мешает отправке, а решение остаётся за сервером (клиент
   * не источник истины по кредитам).
   */
  credits?: number | undefined;
  /**
   * Чем пополнить баланс — реклама, приглашение, что угодно ещё. Мастер
   * показывает этот слот, когда кредитов не осталось, но не знает, что
   * внутри: механики роста не должны просачиваться в экран создания.
   */
  outOfCreditsAction?: ReactNode | undefined;
  /**
   * false — создание ещё не открыто (app_config.generation_open): вместо
   * загрузки фото, согласий и кнопок — «Скоро», параметры можно посмотреть.
   */
  generationOpen?: boolean | undefined;
  onCreate: (request: CreateRequest) => void;
}

type SheetTarget =
  { kind: 'group'; groupId: string } | { kind: 'aspect' } | { kind: 'size' } | null;

export function CreateScreen({
  collection,
  status,
  errorMessage,
  preset,
  credits,
  outOfCreditsAction,
  generationOpen = true,
  onCreate,
}: CreateScreenProps) {
  const [selections, setSelections] = useState(() =>
    preset ? presetSelections(collection, preset) : defaultSelections(collection),
  );
  const [sheet, setSheet] = useState<SheetTarget>(null);
  /** Момент отметки чекбокса — он же уходит в consent.grantedAt. */
  const [consentAt, setConsentAt] = useState<string | null>(null);
  /** Отдельная отметка: согласие на передачу фото за пределы РФ. */
  const [transferAt, setTransferAt] = useState<string | null>(null);

  const { photos, issues, addFiles, remove } = usePhotoSelection({
    max: collection.generation.referenceImages.max,
    // Отдельного readSize здесь нет намеренно: обращение к браузеру спрятано
    // в хуке, а экран остаётся без знаний о том, как измеряется снимок.
  });

  const pending = status === 'pending';
  const conflicts = selectionIssues(collection, selections);
  const missing = describeMissing(
    collection,
    selections,
    photos.length,
    consentAt !== null,
    transferAt !== null,
  );
  // Один портрет — один кредит; серия из N — N кредитов (см. processPhotoshootQueue).
  const outOfCredits = credits !== undefined && credits < 1;
  const canCreate = missing.length === 0 && conflicts.length === 0 && !pending && !outOfCredits;

  const groups = [...collection.optionGroups].sort((left, right) => left.order - right.order);
  const aspectRatio = collection.generation.aspectRatios.find(
    (ratio) => ratio.id === selections.aspectRatioId,
  );
  const seriesSizes = collection.generation.photoshootSizes.filter((size) => size > 1);

  const submit = (photoshootSize: number) => {
    if (!consentAt || !transferAt) {
      return;
    }

    setSheet(null);
    onCreate({
      selections: toUserSelections(collection, selections, photoshootSize),
      photos: photos.map((photo) => photo.file),
      consent: {
        granted: true,
        grantedAt: consentAt,
        consentVersion: CONSENT_VERSION,
        transfer: {
          granted: true,
          grantedAt: transferAt,
          consentVersion: TRANSFER_CONSENT_VERSION,
        },
      },
    });
  };

  return (
    <div className="flex flex-col gap-5 px-4 py-5">
      {generationOpen ? (
        <PhotoPicker
          photos={photos}
          issues={issues}
          max={collection.generation.referenceImages.max}
          disabled={pending}
          onAdd={(files) => void addFiles(files)}
          onRemove={remove}
        />
      ) : (
        <section
          className="rounded-card border border-border bg-surface p-5"
          aria-labelledby="soon-title"
        >
          <h2 id="soon-title" className="font-display text-h2 text-text">
            Скоро
          </h2>
          <p className="mt-2 text-sm text-text-2">
            Создание портретов скоро откроется. Пока можно посмотреть, какие образы есть в
            коллекции.
          </p>
        </section>
      )}

      <section className="flex flex-col gap-2.5" aria-labelledby="parameters-title">
        <h2 id="parameters-title" className="text-sm font-medium text-text-2">
          Параметры портрета
        </h2>

        <div className="overflow-hidden rounded-card border border-border">
          {groups.map((group) => (
            <SummaryRow
              key={group.id}
              label={group.label}
              value={summaryValue(group, selections.groups[group.id] ?? [])}
              preview={selectedPreview(group, selections.groups[group.id] ?? [])}
              required={group.required}
              onOpen={() => setSheet({ kind: 'group', groupId: group.id })}
            />
          ))}

          <SummaryRow
            label="Формат кадра"
            value={aspectRatio?.label ?? null}
            required
            onOpen={() => setSheet({ kind: 'aspect' })}
          />
        </div>
      </section>

      {generationOpen && (
        <>
          <label className="flex items-start gap-2.5 text-xs text-text-2">
            <input
              type="checkbox"
              checked={consentAt !== null}
              onChange={(event) =>
                setConsentAt(event.target.checked ? new Date().toISOString() : null)
              }
              className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            />
            <span>
              {CONSENT_TEXT}{' '}
              <a href="/privacy" className="text-accent hover:underline">
                Политика обработки персональных данных
              </a>
              .
            </span>
          </label>

          <label className="flex items-start gap-2.5 text-xs text-text-2">
            <input
              type="checkbox"
              checked={transferAt !== null}
              onChange={(event) =>
                setTransferAt(event.target.checked ? new Date().toISOString() : null)
              }
              className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
            />
            <span>{TRANSFER_CONSENT_TEXT}</span>
          </label>

          <div className="flex flex-col gap-2">
            {conflicts.map((conflict) => (
              <p key={conflict} className="text-xs text-danger">
                {conflict}
              </p>
            ))}

            {outOfCredits && (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-text-2">
                  Кредиты закончились. Их можно пополнить — за просмотр рекламы или за приглашение
                  друга.
                </p>
                {outOfCreditsAction}
              </div>
            )}

            {errorMessage && <p className="text-sm text-danger">{errorMessage}</p>}

            <button
              type="button"
              disabled={!canCreate}
              onClick={() => submit(1)}
              className="btn btn-primary px-4 py-3"
            >
              Создать портрет
            </button>

            {seriesSizes.length > 0 && (
              <button
                type="button"
                disabled={!canCreate}
                onClick={() => setSheet({ kind: 'size' })}
                className="btn btn-secondary px-4 py-3"
              >
                Создать фотосессию
              </button>
            )}

            {pending && (
              <p className="text-center text-xs text-text-3">
                Создаём — это займёт немного времени.
              </p>
            )}

            {!pending && missing.length > 0 && (
              <p className="text-center text-xs text-text-3">
                Чтобы продолжить: {missing.join(', ')}.
              </p>
            )}
          </div>
        </>
      )}

      {sheet?.kind === 'group' && (
        <GroupSheet
          collection={collection}
          groupId={sheet.groupId}
          selectedIds={selections.groups[sheet.groupId] ?? []}
          onToggle={(optionId, closeAfter) => {
            setSelections((current) => toggleOption(collection, current, sheet.groupId, optionId));
            if (closeAfter) setSheet(null);
          }}
          blockReason={(optionId) =>
            optionBlockReason(collection, selections, sheet.groupId, optionId)
          }
          onClose={() => setSheet(null)}
        />
      )}

      {sheet?.kind === 'aspect' && (
        <BottomSheet title="Формат кадра" onClose={() => setSheet(null)}>
          <div className="grid grid-cols-2 gap-2">
            {collection.generation.aspectRatios.map((ratio) => (
              <button
                key={ratio.id}
                type="button"
                aria-pressed={ratio.id === selections.aspectRatioId}
                onClick={() => {
                  setSelections((current) => ({ ...current, aspectRatioId: ratio.id }));
                  setSheet(null);
                }}
                className={`flex flex-col items-start gap-2 rounded-xl border p-3 text-left ${
                  ratio.id === selections.aspectRatioId
                    ? 'border-accent bg-accent-soft'
                    : 'border-border bg-surface hover:border-border-strong'
                }`}
              >
                <span
                  aria-hidden
                  className="rounded-sm border border-border bg-surface-2"
                  style={{
                    aspectRatio: `${ratio.width} / ${ratio.height}`,
                    width: ratio.width >= ratio.height ? '4rem' : '3rem',
                  }}
                />
                <span className="text-sm text-text">{ratio.label}</span>
              </button>
            ))}
          </div>
        </BottomSheet>
      )}

      {sheet?.kind === 'size' && (
        <BottomSheet
          title="Сколько кадров"
          hint="Каждый кадр списывает один кредит"
          onClose={() => setSheet(null)}
        >
          <div className="grid grid-cols-2 gap-2">
            {seriesSizes.map((size) => {
              const affordable = credits === undefined || credits >= size;

              return (
                <button
                  key={size}
                  type="button"
                  disabled={!affordable}
                  onClick={() => submit(size)}
                  className="flex flex-col gap-1 rounded-xl border border-border bg-surface p-3 text-left hover:border-border-strong disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <span className="text-sm text-text">{size} кадров</span>
                  <span className="text-xs text-text-3">
                    {affordable
                      ? `Спишется ${size} кредитов`
                      : `Не хватает кредитов: есть ${credits}`}
                  </span>
                </button>
              );
            })}
          </div>
        </BottomSheet>
      )}
    </div>
  );
}

interface GroupSheetProps {
  collection: Collection;
  groupId: string;
  selectedIds: string[];
  onToggle: (optionId: string, closeAfter: boolean) => void;
  blockReason: (optionId: string) => string | null;
  onClose: () => void;
}

function GroupSheet({
  collection,
  groupId,
  selectedIds,
  onToggle,
  blockReason,
  onClose,
}: GroupSheetProps) {
  const group = collection.optionGroups.find((candidate) => candidate.id === groupId);

  if (!group) {
    return null;
  }

  return (
    <BottomSheet title={group.label} hint={group.hint} onClose={onClose}>
      <div className="grid grid-cols-2 gap-2">
        {group.options.map((option) => (
          <OptionCard
            key={option.id}
            option={option}
            selected={selectedIds.includes(option.id)}
            blockedReason={blockReason(option.id)}
            // Одиночный выбор завершает шаг сам: держать шторку открытой
            // после него незачем, а лишнее нажатие «Готово» — трение.
            onToggle={() => onToggle(option.id, group.selection === 'single')}
          />
        ))}
      </div>
    </BottomSheet>
  );
}

function summaryValue(group: CollectionOptionGroup, selectedIds: string[]): string | null {
  const labels = selectedIds
    .map((id) => group.options.find((option) => option.id === id)?.label)
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(', ') : null;
}

/** Свотчи палитры показываются прямо в строке — по ним выбор узнаётся быстрее, чем по названию. */
function selectedPreview(group: CollectionOptionGroup, selectedIds: string[]) {
  if (selectedIds.length !== 1) {
    return undefined;
  }

  const preview = group.options.find((option) => option.id === selectedIds[0])?.preview;

  return preview?.kind === 'colors' ? preview : undefined;
}

function describeMissing(
  collection: Collection,
  selections: ReturnType<typeof defaultSelections>,
  photoCount: number,
  consentGranted: boolean,
  transferGranted: boolean,
): string[] {
  const missing: string[] = [];

  if (photoCount < collection.generation.referenceImages.min) {
    missing.push('добавьте фотографию');
  }

  const groups = missingGroups(collection, selections);
  if (groups.length > 0) {
    missing.push(`выберите: ${groups.map((group) => group.label.toLowerCase()).join(', ')}`);
  }

  if (!consentGranted || !transferGranted) {
    missing.push('подтвердите согласия');
  }

  return missing;
}
